import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Typography,
} from "@mui/material";
import {
  MdPayments,
  MdFileDownloadDone,
  MdPendingActions,
  MdRefresh,
  MdQueryStats,
  MdInfoOutline,
  MdTableView,
} from "react-icons/md";
import * as XLSX from "xlsx";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { API_URL } from "../../utils/constants";
import { cssProperties } from "../../utils/commonCssProperties";
import masterActions from "../../redux/master/actions";
import commonActions from "../../redux/common/actions";

/**
 * Download tracking - how far through a course's downloads we are.
 *
 * Shared by the Hall Ticket and Score Card screens: they differ only in which
 * API path they read and what the document is called, so both are props. Two
 * copies would mean every later fix has to be made twice.
 *
 * Paid -> generated -> downloaded. Pending is generated-but-not-downloaded: a
 * paid candidate whose document was never generated has nothing to download,
 * so counting them as pending would make the figure meaningless.
 */

const BRAND = cssProperties?.color?.primary;
const BUTTON_BG = cssProperties?.buttonbgcolor?.primary;
const BUTTON_FG = cssProperties?.buttonfontcolor?.black;
const MUTED = cssProperties?.color?.textsecondary;
const BORDER = cssProperties?.bordercolor?.secondary;

const COLORS = {
  paid: "#0f4f96",
  downloaded: "#1a7f37",
  pending: "#b54708",
};

const panelSx = {
  p: { xs: 2, md: 3 },
  mb: 2.5,
  borderRadius: "16px",
  border: `1px solid ${BORDER}`,
  boxShadow: "0 1px 3px rgba(16, 24, 40, 0.06)",
  backgroundColor: cssProperties?.backgroundcolor?.secondary,
};

const fmt = (n) => (n === null || n === undefined ? "-" : Number(n).toLocaleString("en-IN"));
// Exact percentage - drives the progress bars.
const pct = (part, whole) => (whole ? (part / whole) * 100 : null);

// Caption text, to one decimal place. Whole-number rounding hid real values
// (6 of 1,434 showed as 0%, 1,428 as 100%), so: one decimal, and never a bare
// 0% / 100% while some candidates are on the other side of it.
const fmtPct = (part, whole) => {
  const value = pct(part, whole);
  if (value === null) return null;
  if (part > 0 && value < 0.1) return "<0.1%";
  if (part < whole && value > 99.9) return ">99.9%";
  return `${value.toLocaleString("en-IN", { maximumFractionDigits: 1 })}%`;
};

// Hex colour at low opacity, for icon badges and progress tracks.
const tint = (hex, alpha) => {
  const n = parseInt(hex.slice(1), 16);
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
};

// onClick makes the card an action (e.g. the Excel export): pointer, hover
// outline, keyboard Enter/Space, and an action line under the caption.
const StatCard = ({ label, value, color, icon: Icon, percent, caption, onClick, action, busy }) => (
  <Paper
    {...(onClick && {
      role: "button",
      tabIndex: 0,
      onClick: busy ? undefined : onClick,
      onKeyDown: (e) => {
        if (!busy && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          onClick();
        }
      },
    })}
    sx={{
      ...panelSx,
      mb: 0,
      height: "100%",
      display: "flex",
      flexDirection: "column",
      gap: 1.5,
      ...(onClick && {
        cursor: busy ? "progress" : "pointer",
        transition: "border-color 0.15s, box-shadow 0.15s",
        "&:hover, &:focus-visible": {
          borderColor: color,
          boxShadow: `0 0 0 3px ${tint(color, 0.12)}`,
          outline: "none",
        },
      }),
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
      <Typography sx={{ fontSize: 14, fontWeight: 500, color: MUTED }}>{label}</Typography>
      <Box
        sx={{
          width: 40,
          height: 40,
          flexShrink: 0,
          borderRadius: "12px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color,
          backgroundColor: tint(color, 0.1),
        }}
      >
        <Icon size={22} />
      </Box>
    </Box>

    <Typography sx={{ fontSize: 32, fontWeight: 700, lineHeight: 1.1, color: cssProperties?.fontcolor?.primary }}>
      {fmt(value)}
    </Typography>

    <Box sx={{ mt: "auto" }}>
      {percent !== null && percent !== undefined && (
        <LinearProgress
          variant="determinate"
          value={Math.min(percent, 100)}
          sx={{
            height: 6,
            borderRadius: 3,
            mb: 1,
            backgroundColor: tint(color, 0.12),
            "& .MuiLinearProgress-bar": { borderRadius: 3, backgroundColor: color },
          }}
        />
      )}
      <Typography sx={{ fontSize: 12, color: MUTED }}>{caption}</Typography>
      {action && (
        <Box
          sx={{
            mt: 1.25,
            display: "flex",
            alignItems: "center",
            gap: 0.75,
            fontSize: 13,
            fontWeight: 600,
            color,
          }}
        >
          {busy ? <CircularProgress size={14} sx={{ color }} /> : <MdTableView size={16} />}
          {busy ? "Preparing Excel..." : action}
        </Box>
      )}
    </Box>
  </Paper>
);

const EmptyState = ({ icon: Icon, title, text }) => (
  <Box sx={{ textAlign: "center", py: { xs: 5, md: 7 }, px: 2 }}>
    <Box
      sx={{
        width: 64,
        height: 64,
        mx: "auto",
        mb: 2,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: cssProperties?.hoverbgcolor?.sidebar,
        color: BRAND,
      }}
    >
      <Icon size={30} />
    </Box>
    <Typography sx={{ fontSize: 16, fontWeight: 600, color: cssProperties?.fontcolor?.primary }}>
      {title}
    </Typography>
    {text && (
      <Typography sx={{ mt: 0.5, fontSize: 14, color: MUTED, maxWidth: 440, mx: "auto" }}>
        {text}
      </Typography>
    )}
  </Box>
);

export default function DownloadTracking({ apiPath, documentLabel, canExportDownloaded = false }) {
  const dispatch = useDispatch();
  const { courseList } = useSelector((state) => state.masterReducer);

  const [courseId, setCourseId] = useState(0);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [updatedAt, setUpdatedAt] = useState(null);

  useEffect(() => {
    dispatch({ type: masterActions.GET_COURSE_LIST });
  }, [dispatch]);

  useEffect(() => {
    if (!courseId) {
      setStats(null);
      return;
    }

    let cancelled = false;
    const fetchStats = async () => {
      setLoading(true);
      try {
        const response = await axios.get(
          `${API_URL}/${apiPath}/tracking?courseid=${courseId}`
        );
        if (!cancelled) {
          setStats(response?.data?.result || null);
          setUpdatedAt(new Date());
        }
      } catch (error) {
        if (!cancelled) {
          setStats(null);
          dispatch({
            type: commonActions.SET_ALERT,
            payload: {
              show: true,
              status: "failed",
              message:
                error?.response?.data?.message ||
                `Failed to load ${documentLabel.toLowerCase()} tracking`,
            },
          });
          // The shared toast never closes itself; hide it as other screens do.
          setTimeout(
            () =>
              dispatch({
                type: commonActions.SET_ALERT,
                payload: { status: null, show: false, message: null },
              }),
            2000
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchStats();
    // A quick course switch must not let a slow earlier response overwrite the
    // newer one - the numbers would then belong to the wrong course.
    return () => {
      cancelled = true;
    };
  }, [courseId, refreshKey, dispatch, apiPath, documentLabel]);

  // Both against the first card's total, the only base left on screen.
  const downloadedPct = stats ? pct(stats.downloaded, stats.totalPaid) : null;
  const pendingPct = stats ? pct(stats.pending, stats.totalPaid) : null;
  const downloadedLabel = stats ? fmtPct(stats.downloaded, stats.totalPaid) : null;
  const pendingLabel = stats ? fmtPct(stats.pending, stats.totalPaid) : null;
  const courseName = courseList?.all?.find((c) => c.courseid === courseId)?.coursename;

  const [exporting, setExporting] = useState(false);

  // Excel of the candidates behind the "Downloaded" card. The list is fetched
  // fresh on click, so it matches the database now rather than the figures on
  // screen if those are a few minutes old.
  const exportDownloaded = async () => {
    if (!courseId || exporting) return;

    setExporting(true);
    try {
      const response = await axios.get(
        `${API_URL}/${apiPath}/downloaded-list?courseid=${courseId}`
      );
      const list = response?.data?.result || [];

      if (!list.length) {
        dispatch({
          type: commonActions.SET_ALERT,
          payload: { show: true, status: "failed", message: "No downloads recorded for this course yet" },
        });
        return;
      }

      const sheet = XLSX.utils.json_to_sheet(
        list.map((row, i) => ({
          "Sr. No": i + 1,
          "Application No": row.applicationnumber || "",
          "Roll No": row.rollno || "",
          "Candidate Name": row.candidatename || "",
          "Last Downloaded (IST)": row.lastdownload || "",
          "Venue Code": row.venuecode || "",
          "Venue Name": row.venuename || "",
        }))
      );
      sheet["!cols"] = [{ wch: 7 }, { wch: 16 }, { wch: 14 }, { wch: 32 }, { wch: 26 }, { wch: 12 }, { wch: 48 }];

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, sheet, "Downloaded");

      // Course names contain "/" and "&" - not allowed in file names.
      const safeCourse = (courseName || `Course ${courseId}`).replace(/[\\/:*?"<>|]+/g, "-");
      const today = new Date().toLocaleDateString("en-GB").replace(/\//g, "-");
      XLSX.writeFile(workbook, `${documentLabel} Downloaded - ${safeCourse} - ${today}.xlsx`);
    } catch (error) {
      dispatch({
        type: commonActions.SET_ALERT,
        payload: {
          show: true,
          status: "failed",
          message: error?.response?.data?.message || "Failed to export the downloaded list",
        },
      });
    } finally {
      setExporting(false);
      // The shared toast never closes itself; hide it as other screens do.
      setTimeout(
        () =>
          dispatch({
            type: commonActions.SET_ALERT,
            payload: { status: null, show: false, message: null },
          }),
        2000
      );
    }
  };

  return (
    <Box sx={{ p: { xs: 0, md: 2 } }}>
      {/* Course picker */}
      <Paper sx={panelSx}>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} md={7} lg={6}>
            <Typography sx={{ fontSize: 18, fontWeight: 600, mb: 0.5 }}>
              Course Overview
            </Typography>
            <Typography sx={{ fontSize: 14, color: MUTED, mb: 2 }}>
              Select a course to see how many registered candidates have downloaded their{" "}
              {documentLabel.toLowerCase()} and how many are still pending.
            </Typography>
            <Typography sx={{ mb: 0.75, fontSize: 13, fontWeight: 500, color: MUTED }}>
              Course
            </Typography>
            <Select
              fullWidth
              size="small"
              value={courseId}
              displayEmpty
              sx={{ borderRadius: "10px", backgroundColor: "#fff" }}
              onChange={(e) => {
                // Old figures must not sit under the new course name while it loads.
                setStats(null);
                setCourseId(e.target.value);
              }}
            >
              <MenuItem value={0} disabled>
                <Typography component="span" sx={{ color: MUTED }}>
                  Select a course
                </Typography>
              </MenuItem>
              {courseList?.all?.map((course, i) => (
                <MenuItem key={i} value={course.courseid} sx={{ whiteSpace: "nowrap" }}>
                  {course.coursename}
                </MenuItem>
              ))}
            </Select>
          </Grid>

          {courseId ? (
            <Grid
              item
              xs={12}
              md={5}
              lg={6}
              sx={{
                display: "flex",
                alignItems: { xs: "stretch", md: "flex-end" },
                flexDirection: "column",
                gap: 0.75,
              }}
            >
              <Button
                variant="outlined"
                onClick={() => setRefreshKey((k) => k + 1)}
                disabled={loading}
                startIcon={
                  loading ? <CircularProgress size={16} sx={{ color: BUTTON_FG }} /> : <MdRefresh />
                }
                sx={{
                  borderRadius: "40px",
                  px: 3,
                  py: 1,
                  fontWeight: 600,
                  color: BUTTON_FG,
                  border: `1px solid ${BUTTON_BG}`,
                  "&:hover": {
                    border: `1px solid ${BUTTON_BG}`,
                    backgroundColor: "rgba(230, 151, 171, 0.08)",
                  },
                }}
              >
                {loading ? "Refreshing..." : "Refresh"}
              </Button>
              {updatedAt && (
                <Typography sx={{ fontSize: 12, color: MUTED, textAlign: { xs: "center", md: "right" } }}>
                  Last updated{" "}
                  {updatedAt.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                </Typography>
              )}
            </Grid>
          ) : null}
        </Grid>
      </Paper>

      {/* Nothing selected */}
      {!courseId && (
        <Paper sx={panelSx}>
          <EmptyState
            icon={MdQueryStats}
            title="Select a course"
            text={`${documentLabel} figures for the course will appear here.`}
          />
        </Paper>
      )}

      {/* First load for a course */}
      {courseId && loading && !stats ? (
        <Paper sx={{ ...panelSx, display: "flex", justifyContent: "center", py: 8 }}>
          <CircularProgress />
        </Paper>
      ) : null}

      {/* Load failed / no data */}
      {courseId && !loading && !stats ? (
        <Paper sx={panelSx}>
          <EmptyState
            icon={MdQueryStats}
            title="No data for this course"
            text="Try Refresh, or pick another course."
          />
        </Paper>
      ) : null}

      {/* Figures - kept on screen (dimmed) during a refresh so the page doesn't jump */}
      {courseId && stats ? (
        <Box sx={{ opacity: loading ? 0.55 : 1, transition: "opacity 0.2s" }}>
          {courseName && (
            <Typography sx={{ fontSize: 15, fontWeight: 600, mb: 1.5, px: 0.5 }}>
              {courseName}
            </Typography>
          )}

          <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
            <Grid item xs={12} md={4}>
              <StatCard
                label="Total Candidates Registered"
                value={stats.totalPaid}
                color={COLORS.paid}
                icon={MdPayments}
                percent={stats.totalPaid ? 100 : null}
                caption="Payment completed for this course"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <StatCard
                label={`${documentLabel} Downloaded`}
                value={stats.downloaded}
                color={COLORS.downloaded}
                icon={MdFileDownloadDone}
                percent={downloadedPct}
                caption={downloadedLabel === null ? "-" : `${downloadedLabel} of registered candidates`}
                {...(canExportDownloaded &&
                  stats.downloaded > 0 && {
                    onClick: exportDownloaded,
                    action: "Click to download Excel",
                    busy: exporting,
                  })}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <StatCard
                label="Pending Downloads"
                value={stats.pending}
                color={COLORS.pending}
                icon={MdPendingActions}
                percent={pendingPct}
                caption={
                  pendingLabel === null
                    ? `${documentLabel} available, not downloaded yet`
                    : `${pendingLabel} of registered candidates not downloaded yet`
                }
              />
            </Grid>
          </Grid>

          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1.25,
              p: 2,
              borderRadius: "12px",
              backgroundColor: cssProperties?.hoverbgcolor?.sidebar,
            }}
          >
            <MdInfoOutline size={20} color={BRAND} style={{ flexShrink: 0, marginTop: 1 }} />
            <Typography sx={{ fontSize: 14, color: cssProperties?.fontcolor?.primary }}>
              <b>{fmt(stats.totalDownloads)}</b> download{stats.totalDownloads === 1 ? "" : "s"} in
              total &mdash; candidates who downloaded more than once are counted once above.
            </Typography>
          </Box>
        </Box>
      ) : null}
    </Box>
  );
}
